import {Client} from "@stomp/stompjs";
import SockJS from "sockjs-client";
import axios from "./axiosInstance";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) 전용 웹소켓 접속 모듈
 * ════════════════════════════════════════════════════════════════════════════════
 *  [기존 WebsocketService.js 와 분리한 이유]
 *   1) 접속주체가 다르다.
 *      기존 모듈은 '비토리지 회원(mbNum)' 이 CONNECT 헤더 key 로 붙는 것을 전제로 하고,
 *      회원 전용 목적지(/user/{mbNum}/queue/...)를 함께 쓴다.
 *      상점사는 비토리지 회원이 아니며 별도 토큰(X-STORE-TOKEN)으로 인증한다.
 *   2) 하나의 커넥션을 공유하면 서로의 key 를 덮어쓴다.
 *      기존 WebsocketContext 는 '이미 연결되어 있으면 그대로 반환' 하므로,
 *      관리자가 로그인한 브라우저에서 상점사 채팅을 먼저 열면 이후 회원용 연결요청이
 *      상점사 key 로 맺어진 커넥션을 그대로 돌려받아 /user/{mbNum}/queue/... 수신이 끊긴다.
 *      (반대 순서도 동일)
 *   -> 그래서 상점사 채팅은 '완전히 독립된 커넥션' 을 사용한다.
 *      기존 회원/관리자 채팅 로직은 이 파일을 전혀 참조하지 않으므로 영향이 없다.
 *
 *  [기존 대비 개선점]
 *   - 접속주소를 axios.defaults.baseURL 에서 파생시켜 단일 소스로 관리한다.
 *     (기존 WebsocketService.js 는 주소가 하드코딩되어 개발/운영 전환 시 수정누락 위험이 있었다)
 *   - 재연결을 stompjs 내장 기능(reconnectDelay)에 맡긴다.
 *     기존처럼 setInterval 로 별도 재연결까지 돌리면 라이브러리 재연결과 겹쳐
 *     커넥션이 중복 생성된다.
 *   - webSocket.onclose 를 직접 덮어쓰지 않는다.
 *     그 핸들러는 stompjs 가 내부정리에 사용하므로 덮어쓰면 재연결이 깨진다.
 *     공식 콜백인 onWebSocketClose 를 사용한다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** STOMP 재연결 간격 (ms) */
export const STORE_WS_RECONNECT_DELAY = 5000;
/** 하트비트 간격 (ms) — 백엔드 WebSocketConfig 의 설정값과 동일하게 맞춘다 */
export const STORE_WS_HEARTBEAT = 20000;

/**
 * 상점사 웹소켓 접속주소
 *  - axios.defaults.baseURL 예) https://btorage-dev.com/api
 *  - 최종주소  예) https://btorage-dev.com/api/websocket-sockjs
 */
export const getStoreSocketUrl = () => {
    const base = (axios.defaults.baseURL || '').replace(/\/+$/, '');
    if (base) return `${base}/websocket-sockjs`;
    // baseURL 이 비어있는 경우(동일 출처 배포)에는 현재 도메인 기준으로 접속한다.
    return `${window.location.origin}/api/websocket-sockjs`;
};

/**
 * 상점사 전용 STOMP 클라이언트 생성
 *  @param storeKey CONNECT 헤더로 보낼 식별값 (예: 'store-12', 'adminStoreChat')
 */
export const createStoreClient = (storeKey) => {
    return new Client({
        webSocketFactory: () => new SockJS(getStoreSocketUrl()),
        connectHeaders: {key: storeKey ? String(storeKey) : ''},
        reconnectDelay: STORE_WS_RECONNECT_DELAY,
        heartbeatIncoming: STORE_WS_HEARTBEAT,
        heartbeatOutgoing: STORE_WS_HEARTBEAT,
        // STOMP 프레임 원문이 콘솔을 가리지 않도록 기본 디버그 로그는 끈다.
        debug: () => {
        },
    });
};
